import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight, ChevronDown, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND } from "../lib/brand";
import { api } from "../lib/api";

const links = [
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/case-studies", label: "Work" },
  { to: "/pricing", label: "Pricing" },
  { to: "/audit", label: "Free Audit" },
  { to: "/cities-served", label: "Cities Served" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const PHONES = [
  { id: "us", flag: "🇺🇸", country: "USA", display: BRAND.phoneDisplay, tel: BRAND.phoneTel },
  { id: "uk", flag: "🇬🇧", country: "UK", display: BRAND.uk.phoneDisplay, tel: BRAND.uk.phoneTel },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [platformsOpen, setPlatformsOpen] = useState(false);
  const [hubs, setHubs] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setPlatformsOpen(false); }, [location.pathname]);

  // Fetch platform hubs once for the dropdown — no hardcoded list.
  useEffect(() => {
    let alive = true;
    api.get("/hubs").then((r) => { if (alive) setHubs(r.data.hubs || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between text-white">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
          <img
            src="/logo.png"
            alt="WebsiteAndGo logo"
            width="44"
            height="44"
            className="w-11 h-11 object-contain group-hover:scale-[1.06] transition-transform"
            loading="eager"
            decoding="async"
          />
          <span className="font-display text-lg font-semibold tracking-tight hidden sm:inline lg:hidden xl:inline">
            WebsiteAndGo
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links
            .filter((l) => l.to !== "/industries" && l.to !== "/case-studies")
            .map((l, idx) => (
            <div key={l.to} className={`items-center ${l.to === "/cities-served" ? "hidden 2xl:flex" : l.to === "/blog" ? "hidden xl:flex" : "flex"}`}>
              <NavLink
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-2.5 xl:px-3 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-white bg-white/10"
                      : "text-white/70 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
              {/* Insert Platforms dropdown right after Services (Industries hidden on desktop) */}
              {l.to === "/services" && hubs.length > 0 && (
                <div
                  className="relative"
                  onMouseEnter={() => setPlatformsOpen(true)}
                  onMouseLeave={() => setPlatformsOpen(false)}
                  data-testid="nav-platforms-wrap"
                >
                  <button
                    type="button"
                    data-testid="nav-platforms"
                    className="px-3 py-2 text-sm font-medium rounded-full transition-colors text-white/70 hover:text-white inline-flex items-center gap-1"
                    onClick={() => setPlatformsOpen(v => !v)}
                    aria-haspopup="true"
                    aria-expanded={platformsOpen}
                  >
                    Platforms <ChevronDown className={`w-3 h-3 transition-transform ${platformsOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {platformsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.15 }}
                        data-testid="nav-platforms-menu"
                        className="absolute top-full left-0 mt-1 w-64 rounded-xl border border-white/10 bg-black/95 backdrop-blur-xl shadow-xl overflow-hidden"
                      >
                        {hubs.map((h) => (
                          <NavLink
                            key={h.slug}
                            to={`/${h.slug}`}
                            data-testid={`nav-platform-${h.slug}`}
                            className={({ isActive }) =>
                              `block px-4 py-3 text-sm border-b border-white/5 last:border-0 transition ${
                                isActive ? "text-[#FF6A00] bg-white/5" : "text-white/80 hover:bg-white/5 hover:text-white"
                              }`
                            }
                          >
                            <div className="font-semibold">{h.name}</div>
                            {h.tagline && <div className="text-[11px] text-white/50 mt-0.5 line-clamp-1">{h.tagline}</div>}
                          </NavLink>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <div className="flex 2xl:hidden items-center gap-1.5" data-testid="header-phones-compact">
            {PHONES.map((ph) => <PhoneChip key={ph.id} ph={ph} testid={`header-phone-chip-${ph.id}`} />)}
          </div>
          <div className="hidden 2xl:flex flex-col gap-0.5" data-testid="header-phones">
            {PHONES.map((ph) => (
              <a key={ph.id} href={ph.tel} data-testid={`header-phone-${ph.id}`} aria-label={`Call WebsiteAndGo ${ph.country}`}
                className="flex items-center gap-1.5 text-xs text-white/80 hover:text-white transition-colors whitespace-nowrap">
                <span aria-hidden className="text-sm leading-none">{ph.flag}</span>
                <span className="font-mono tabular-nums">{ph.display}</span>
              </a>
            ))}
          </div>
          <Link
            to="/free-website-review"
            data-testid="header-cta"
            className="btn-primary text-sm whitespace-nowrap"
          >
            Free Website Review <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-1.5" data-testid="header-mobile-phones">
          {PHONES.map((ph) => <PhoneChip key={ph.id} ph={ph} testid={`header-mobile-phone-${ph.id}`} />)}
        <button
          data-testid="header-mobile-toggle"
          className="lg:hidden text-white p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-black border-t border-white/10"
          >
            <div className="px-6 pt-6 pb-32 flex flex-col gap-1">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="py-3 text-base text-white/80 hover:text-white border-b border-white/5"
                >
                  {l.label}
                </Link>
              ))}
              {hubs.length > 0 && (
                <div className="mt-2 pt-3 border-t border-white/10" data-testid="mobile-nav-platforms">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/45 mb-2">Platforms</div>
                  <div className="flex flex-wrap gap-1.5">
                    {hubs.map((h) => (
                      <Link
                        key={h.slug}
                        to={`/${h.slug}`}
                        data-testid={`mobile-nav-platform-${h.slug}`}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-white/80 hover:border-[#FF6A00]/50 hover:text-white"
                      >
                        {h.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="mobile-menu-phones">
                {PHONES.map((ph) => (
                  <a key={ph.id} href={ph.tel} data-testid={`mobile-menu-phone-${ph.id}`}
                    className="flex items-center gap-2 py-3 px-4 rounded-xl border border-white/10 bg-white/5 text-sm text-white/90">
                    <span aria-hidden>{ph.flag}</span>
                    <span className="text-white/50 text-xs">{ph.country}</span>
                    <span className="font-mono tabular-nums ml-auto">{ph.display}</span>
                  </a>
                ))}
              </div>
              <Link
                to="/free-website-review"
                data-testid="mobile-header-cta"
                className="btn-primary mt-4 justify-center"
              >
                Free Website Review <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function PhoneChip({ ph, testid }) {
  return (
    <a href={ph.tel} data-testid={testid} aria-label={`Call WebsiteAndGo ${ph.country}: ${ph.display}`} title={`${ph.country}: ${ph.display}`}
      className="inline-flex items-center gap-1 h-9 px-2.5 rounded-full border border-white/15 bg-white/5 text-xs text-white/90 hover:border-[#FF6A00]/60 transition-colors">
      <span aria-hidden className="text-sm leading-none">{ph.flag}</span>
      <Phone className="w-3.5 h-3.5 text-[#FF6A00]" />
    </a>
  );
}
